/**
 * GSC Links Management API Service
 * 
 * Provides API calls for GSC external backlinks, linking domains,
 * internal links, and link change tracking.
 */

import customerApi from './customerApi';
import type {
  ExternalLinksResponse,
  ExternalLinksSummaryDTO,
  LinkingDomainsResponse,
  DomainDetailDTO,
  TopLinkedPagesResponse,
  LinkChangesResponse,
  DailyChangeCountsDTO,
  InternalLinksResponse,
  InternalLinksSummaryDTO,
  InternalOrphanPagesResponse,
  BrokenInternalLinksResponse,
  InternalSourcePagesResponse,
  InternalTargetPagesResponse
} from '../../types/customer';

const API_BASE = '/customer/links';

/**
 * Get paginated external backlinks for a property
 */
export const getExternalLinks = async (
  page: number = 0,
  size: number = 20,
  sourceDomain?: string
): Promise<ExternalLinksResponse> => {
  const params: Record<string, any> = {
    page,
    size
  };
  if (sourceDomain) {
    params.sourceDomain = sourceDomain;
  }
  const response = await customerApi.get(`${API_BASE}/external`, { params });
  return response.data;
};

/**
 * Get external links summary metrics
 */
export const getExternalLinksSummary = async (
): Promise<ExternalLinksSummaryDTO> => {
  const response = await customerApi.get(`${API_BASE}/external/summary`);
  return response.data;
};

/**
 * Get paginated linking domains with filters
 */
export const getLinkingDomains = async (
  page: number = 0,
  size: number = 20,
  minAuthority?: number,
  maxSpam?: number
): Promise<LinkingDomainsResponse> => {
  const params: Record<string, any> = {
    page,
    size
  };
  if (minAuthority !== undefined) {
    params.minAuthority = minAuthority;
  }
  if (maxSpam !== undefined) {
    params.maxSpam = maxSpam;
  }
  const response = await customerApi.get(`${API_BASE}/domains`, { params });
  return response.data;
};

/**
 * Get detailed information for a specific domain
 */
export const getDomainDetail = async (
  sourceDomain: string
): Promise<DomainDetailDTO> => {
  const response = await customerApi.get(`${API_BASE}/domains/${encodeURIComponent(sourceDomain)}`);
  return response.data;
};

/**
 * Get top linked pages (pages with most backlinks)
 */
export const getTopLinkedPages = async (
  page: number = 0,
  size: number = 20
): Promise<TopLinkedPagesResponse> => {
  const response = await customerApi.get(`${API_BASE}/top-linked-pages`, {
    params: { page, size }
  });
  return response.data;
};

/**
 * Get link changes (gains/losses) within date range
 */
export const getLinkChanges = async (
  startDate: string,
  endDate: string,
  page: number = 0,
  size: number = 20,
  changeType?: 'GAINED' | 'LOST',
  linkType?: 'EXTERNAL' | 'INTERNAL'
): Promise<LinkChangesResponse> => {
  const params: Record<string, any> = {
    startDate,
    endDate,
    page,
    size
  };
  if (changeType) {
    params.changeType = changeType;
  }
  if (linkType) {
    params.linkType = linkType;
  }
  const response = await customerApi.get(`${API_BASE}/changes`, { params });
  return response.data;
};

/**
 * Get daily change counts (time series data for charts)
 */
export const getDailyChangeCounts = async (
  startDate: string,
  endDate: string,
  linkType?: 'EXTERNAL' | 'INTERNAL'
): Promise<DailyChangeCountsDTO> => {
  const params: Record<string, any> = { startDate, endDate };
  if (linkType) {
    params.linkType = linkType;
  }
  const response = await customerApi.get(`${API_BASE}/changes/daily-counts`, { params });
  return response.data;
};

/**
 * Get internal links for latest snapshot
 */
export const getInternalLinks = async (
  page: number = 0,
  size: number = 20,
  sourcePage?: string,
  targetPage?: string
): Promise<InternalLinksResponse> => {
  const params: Record<string, any> = {
    page,
    size
  };
  if (sourcePage) {
    params.sourcePage = sourcePage;
  }
  if (targetPage) {
    params.targetPage = targetPage;
  }
  const response = await customerApi.get(`${API_BASE}/internal`, { params });
  return response.data;
};

/**
 * Get internal links summary metrics
 */
export const getInternalLinksSummary = async (
): Promise<InternalLinksSummaryDTO> => {
  const response = await customerApi.get(`${API_BASE}/internal/summary`);
  return response.data;
};

/**
 * Get orphan pages (no inbound links)
 */
export const getInternalOrphanPages = async (): Promise<InternalOrphanPagesResponse> => {
  const response = await customerApi.get(`${API_BASE}/internal/orphans`);
  return response.data;
};

/**
 * Get broken internal links whose target page returns 4xx/5xx.
 */
export const getBrokenInternalLinks = async (
  page: number = 0,
  size: number = 20
): Promise<BrokenInternalLinksResponse> => {
  const response = await customerApi.get(`${API_BASE}/internal/broken`, {
    params: { page, size }
  });
  return response.data;
};

/**
 * Get internal source pages summary (outbound links)
 */
export const getInternalSourcePages = async (
  page: number = 0,
  size: number = 20
): Promise<InternalSourcePagesResponse> => {
  const response = await customerApi.get(`${API_BASE}/internal/source-pages`, {
    params: { page, size }
  });
  return response.data;
};

/**
 * Get internal target pages summary (inbound links)
 */
export const getInternalTargetPages = async (
  page: number = 0,
  size: number = 20
): Promise<InternalTargetPagesResponse> => {
  const response = await customerApi.get(`${API_BASE}/internal/target-pages`, {
    params: { page, size }
  });
  return response.data;
};

/**
 * Get available snapshot dates for internal links
 * Returns list of dates when internal link data was captured
 */
export const getInternalLinksSnapshots = async (): Promise<string[]> => {
  const response = await customerApi.get(`${API_BASE}/internal/snapshots`);
  return response.data;
};
